import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { createKeyboardClickHandler } from '@/utils/accessibility';

import { CropImage } from '@/components/CropImage/CropImage';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import {
  DEFAULT_STRAP_REFERENCE_DIMENSIONS,
  inferSourceDimensionsFromUrl,
} from '@/lib/cdn/cropTransform';
import { generateDataTestId } from '@/lib/tests';
import { pdpMessages } from '@/messages/product';

interface ChangeStrapButtonProps {
  coords: { x1: number; x2: number; y1: number; y2: number };
  name: string;
  setIsChangeStrapModalOpen: (isOpen: boolean) => void;
  url: string;
}

const getPreviewCoords = (
  coords: { x1: number; x2: number; y1: number; y2: number },
  width: number,
  height: number,
) => {
  const x = (coords.x1 + coords.x2) / 2;
  const y = (coords.y1 + coords.y2) / 2;
  const new_x1 = x - width / 2;
  const new_y1 = y - height / 2;
  const new_x2 = x + width / 2;
  const new_y2 = y + height / 2;
  return { x1: new_x1, y1: new_y1, x2: new_x2, y2: new_y2 };
};

const ButtonContainer = styled.div`
  display: inline-flex;
  margin-bottom: 1em;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  border: 4px solid #fff;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  height: 36px;
  width: 36px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  backface-visibility: hidden;
  transform: translateZ(0);
  perspective: 1000;
  will-change: transform;
  -webkit-backface-visibility: hidden;
  -webkit-transform: translateZ(0);
  -webkit-perspective: 1000;
`;

const ChangeStrapButton: React.FC<ChangeStrapButtonProps> = ({
  coords,
  name,
  setIsChangeStrapModalOpen,
  url,
}) => {
  const { formatMessage } = useIntl();
  const inferredSourceDimensions = inferSourceDimensionsFromUrl(
    url,
    DEFAULT_STRAP_REFERENCE_DIMENSIONS,
  );
  const sourceImageDimensions = inferredSourceDimensions
    ? {
        x: inferredSourceDimensions.width,
        y: inferredSourceDimensions.height,
      }
    : undefined;
  return (
    <ButtonContainer
      data-test-id='ChangeStrapButtonContainer'
      onClick={() => setIsChangeStrapModalOpen(true)}
      onKeyDown={createKeyboardClickHandler(() =>
        setIsChangeStrapModalOpen(true),
      )}
      role='button'
      tabIndex={0}
    >
      <ImageContainer data-test-id='ChangeStrapButtonImageContainer'>
        <CropImage
          alt={name}
          coords={getPreviewCoords(coords, 400, 500)}
          data-test-id='ChangeStrapButtonCropImage'
          height={41}
          loading='lazy'
          mobileWidth={30}
          originalImageDimensions={{
            x: DEFAULT_STRAP_REFERENCE_DIMENSIONS.width,
            y: DEFAULT_STRAP_REFERENCE_DIMENSIONS.height,
          }}
          sizes='50vw'
          sourceImageDimensions={sourceImageDimensions}
          src={url}
          strapCropPreset='circle'
          useLoader={false}
          width={30}
          {...generateDataTestId('productCard', 'image')}
        />
      </ImageContainer>
      <LegacyButton
        label={formatMessage(pdpMessages.skuChange)}
        ml={1}
        size='l'
        variant='link'
        width='auto'
      >
        <Icon name='chevronRight' />
      </LegacyButton>
    </ButtonContainer>
  );
};

export default ChangeStrapButton;
